import React, { useState, useEffect } from 'react';
import { useConfirm, useAlert } from '../components/ConfirmModal';
import { Link } from 'react-router-dom';
import DashboardLayout from '../components/shared/DashboardLayout';
import { useAuth } from '../context/AuthContext';
import { useTheme } from '../context/ThemeContext';
import useSettings from '../utils/useSettings';
import API from '../utils/api';
import useLimits from '../utils/useLimits';
import TopUpModal from '../components/TopUpModal';
import { trackChildCreated, trackChildDeleted, trackChildPhotoUploaded, trackChildPhotoDeleted } from '../utils/analytics';
import { copyToClipboard } from '../utils/clipboard';
import { isViaCoParent, entitledPlan } from '../utils/entitlements';

const AVATARS = ['🧒','👦','👧','🧑','👶','🦸','🧙','🧜','🦄','🌟','🦁','🐻','🦊','🐼','🐨','🦋','🐸','🐶','🐱','🐭','🐰'];
const INTERESTS_OPTIONS = ['Dinosaurs','Space','Animals','Magic','Superheroes','Princesses','Cars','Nature','Music','Art','Sports','Ocean'];
const TONES = ['magical','adventurous','funny','calm','educational','cozy','exciting','spooky'];
const DAYS = ['monday','tuesday','wednesday','thursday','friday','saturday','sunday'];
const DAY_LABELS = ['Mon','Tue','Wed','Thu','Fri','Sat','Sun'];

const SCHEDULER_THEMES = [
  '',
  'Kindness','Bravery','Friendship','Sharing','Honesty','Patience',
  'Curiosity','Gratitude','Perseverance','Empathy','Courage','Generosity',
  'New Sibling','Starting School','Fear of the Dark','Moving Home',
  'Making New Friends','Feeling Nervous','Losing a Pet',
  '🎃 Halloween','🎄 Christmas','🌸 Spring','☀️ Summer','🍂 Autumn','❄️ Winter',
];

const TONE_INFO = {
  magical: '✨', adventurous: '🗺️', funny: '😄', calm: '🌙',
  educational: '📚', cozy: '🔥', exciting: '⚡', spooky: '👻',
};

const emptySchedule = { monday:'', tuesday:'', wednesday:'', thursday:'', friday:'', saturday:'', sunday:'' };

const LEARNING_TOPICS = [
  { category: 'Science & Nature', topics: ['The Solar System', 'Rainforests', 'Dinosaurs', 'Ocean Life', 'Weather & Seasons', 'Plants & Flowers', 'Volcanoes', 'Animals & Habitats'] },
  { category: 'Maths & Numbers', topics: ['Counting & Numbers', 'Shapes & Patterns', 'Addition & Subtraction', 'Telling the Time', 'Fractions', 'Measurement'] },
  { category: 'Life Skills', topics: ['Being Kind', 'Sharing & Taking Turns', 'Managing Emotions', 'Healthy Habits', 'Making Friends', 'Tidying Up', 'Teamwork'] },
  { category: 'Language & Arts', topics: ['Rhyming & Poetry', 'Storytelling', 'Letters & Alphabet', 'Colours & Art', 'Music & Rhythm'] },
  { category: 'History & Culture', topics: ['Ancient Egypt', 'Knights & Castles', 'Space Exploration', 'World Cultures', 'Famous Explorers'] },
];

const READING_LEVEL_LABELS = {
  1: 'Toddler',  2: 'Toddler',
  3: 'Pre-reader', 4: 'Pre-reader',
  5: 'Early reader', 6: 'Early reader',
  7: 'Developing reader', 8: 'Developing reader',
  9: 'Fluent reader', 10: 'Fluent reader',
  11: 'Advanced reader', 12: 'Advanced reader',
};

const defaultForm = {
  name:'', age:'', avatar:'🧒', gender:'neutral', interests:[],
  favoriteCharacters:'', weeklyTheme:'',
  weeklySchedule: { ...emptySchedule },
  storyPreferences:{ length:'medium', tone:'magical' },
  strictMode: false,
  requireApproval: false,
  readingLevel: null, // null = auto
  learningGoals: [],
};

const ChildrenPage = () => {
  const confirm = useConfirm();
  const alertModal = useAlert();
  const { darkMode: dm } = useTheme();
  const [children, setChildren] = useState([]);
  const [loading, setLoading] = useState(true);
  const [showForm, setShowForm] = useState(false);
  const [editing, setEditing] = useState(null);
  const [form, setForm] = useState(defaultForm);
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState('');
  const [photoUploading, setPhotoUploading] = useState(false);
  const [photoError, setPhotoError] = useState('');
  const [photoPreviewLoading, setPhotoPreviewLoading] = useState(false);
  const [showPhotoTopUp, setShowPhotoTopUp] = useState(false);
  const [photoPreviewUrl, setPhotoPreviewUrl] = useState(null);
  const [customGoalInput, setCustomGoalInput] = useState('');
  const { user } = useAuth();
  // Creating, editing, deleting a child, managing their photo and issuing
  // grandparent links are all primary-parent-only in the API
  // (childrenController and grandparentController all return 403 on
  // !req.isPrimaryParent). The UI showed every one of those controls to
  // co-parents anyway, so a grandparent or spouse invited into the family saw a
  // page of buttons that each failed with an error toast.
  const canManageChildren = !isViaCoParent(user);
  const hasPaidPlan = entitledPlan(user) !== 'free';

  // Grandparent share link state
  const [gpModal, setGpModal] = useState(null);    // { childId, childName, childAvatar }
  const [gpLink, setGpLink] = useState(null);      // { token, enabled, viewCount, lastViewedAt, url }
  const [gpLoading, setGpLoading] = useState(false);
  const [gpCopied, setGpCopied] = useState(false);
  const [gpToggling, setGpToggling] = useState(false);
  const [gpRevoking, setGpRevoking] = useState(false);
  const { remaining, refresh: refreshLimits } = useLimits();
  const photoCreditsLeft = remaining.photoIllustrations || 0;
  const { settings } = useSettings();
  const flags = settings.featureFlags || {};

  const fetchChildren = () => {
    setLoading(true);
    API.get('/children').then(res => setChildren(res.data.children)).catch(console.error).finally(() => setLoading(false));
  };

  useEffect(() => { fetchChildren(); }, []);

  const fetchPhotoPreview = async (childId) => {
    setPhotoPreviewUrl(prev => { if (prev) URL.revokeObjectURL(prev); return null; });
    setPhotoPreviewLoading(true);
    try {
      const res = await API.get(`/children/${childId}/photo`, { responseType: 'blob' });
      setPhotoPreviewUrl(URL.createObjectURL(res.data));
    } catch (err) {
      console.error('[fetchPhotoPreview] failed:', err?.response?.status, err?.message);
      setPhotoPreviewUrl(null);
    } finally {
      setPhotoPreviewLoading(false);
    }
  };

  const openAdd = () => {
    setEditing(null);
    setForm({ ...defaultForm });
    setPhotoPreviewUrl(null); setCustomGoalInput(''); setError(''); setShowForm(true);
  };
  const openEdit = (child) => {
    setEditing(child._id);
    setForm({
      ...child,
      hasPhoto: !!child.photoPublicId,
      favoriteCharacters: child.favoriteCharacters?.join(', ') || '',
      weeklySchedule: { ...emptySchedule, ...(child.weeklySchedule || {}) },
      learningGoals: child.learningGoals || [],
    });
    setCustomGoalInput('');
    setError(''); setShowForm(true);
    if (child.photoPublicId) fetchPhotoPreview(child._id);
    else setPhotoPreviewUrl(null);
  };

  const handleSave = async (e) => {
    e.preventDefault(); setSaving(true); setError('');
    try {
      // Exclude read-only photo fields and learningGoals (saved via dedicated endpoint)
      const { photoPublicId, photoDescription, hasPhoto, learningGoals, ...formRest } = form;
      const payload = {
        ...formRest,
        favoriteCharacters: form.favoriteCharacters.split(',').map(s => s.trim()).filter(Boolean)
      };
      let childId = editing;
      if (editing) {
        await API.put(`/children/${editing}`, payload);
      } else {
        const res = await API.post('/children', payload);
        childId = res.data.child._id;
        trackChildCreated(Number(form.age) || null);
      }
      // Save learning goals via dedicated endpoint (validated separately)
      if (childId) {
        await API.put(`/children/${childId}/goals`, { learningGoals: learningGoals || [] });
      }
      fetchChildren(); setShowForm(false);
    } catch (err) {
      setError(err.response?.data?.message || 'Failed to save.');
    }
    finally { setSaving(false); }
  };

  const handleDelete = async (id) => {
    const ok = await confirm({ title: 'Delete child profile?', message: 'The profile will be removed. Their stories will remain in your library.', confirmLabel: 'Delete', danger: true });
    if (!ok) return;
    // The failure path here was swallowed to console: the dialog closed, a
    // "deleted" analytics event fired, and the child silently reappeared.
    try {
      await API.delete(`/children/${id}`);
      trackChildDeleted();
      fetchChildren();
    } catch (err) {
      await alertModal({
        title: 'Could not delete profile',
        message: err.response?.data?.message || 'Please try again.',
        danger: true,
      });
    }
  };

  const handlePhotoUpload = async (e) => {
    const file = e.target.files?.[0];
    if (!file || !editing) return;
    setPhotoUploading(true);
    setPhotoError('');
    try {
      const fd = new FormData();
      fd.append('photo', file);
      const { data } = await API.post(`/children/${editing}/photo`, fd, {
        headers: { 'Content-Type': 'multipart/form-data' },
      });
      setForm(f => ({
        ...f,
        hasPhoto: data.hasPhoto,
        photoDescription: data.photoDescription,
        usePhotoInIllustrations: data.usePhotoInIllustrations,
      }));
      trackChildPhotoUploaded();
      fetchChildren();
      if (editing) fetchPhotoPreview(editing);
    } catch (err) {
      setPhotoError(err.response?.data?.message || 'Photo upload failed.');
    } finally {
      setPhotoUploading(false);
      e.target.value = '';
    }
  };

  const handlePhotoDelete = async () => {
    if (!editing) return;
    const ok = await confirm({ title: 'Remove reference photo?', message: 'The photo and appearance description will be deleted. Future illustrations will no longer use your child\'s likeness.', confirmLabel: 'Remove', danger: true });
    if (!ok) return;
    try {
      await API.delete(`/children/${editing}/photo`);
      trackChildPhotoDeleted();
      setForm(f => ({ ...f, hasPhoto: false, photoDescription: null, usePhotoInIllustrations: false }));
      setPhotoPreviewUrl(null);
      fetchChildren();
    } catch (err) {
      setPhotoError(err.response?.data?.message || 'Failed to remove photo.');
    }
  };

  const openGpModal = async (child) => {
    setGpModal({ childId: child._id, childName: child.name, childAvatar: child.avatar });
    setGpLink(null); setGpCopied(false); setGpLoading(true);
    try {
      const res = await API.get(`/grandparent/child/${child._id}/link`);
      setGpLink(res.data.link);
    } catch (err) {
      setGpLink({ error: err.response?.data?.message || 'Failed to load link.' });
    } finally { setGpLoading(false); }
  };

  const closeGpModal = () => { setGpModal(null); setGpLink(null); setGpCopied(false); };

  const handleCopyGpLink = () => {
    if (!gpLink?.url) return;
    copyToClipboard(gpLink.url).then((ok) => {
      if (!ok) return;
      setGpCopied(true);
      setTimeout(() => setGpCopied(false), 2500);
    });
  };

  const handleToggleGpLink = async (enabled) => {
    if (!gpModal) return;
    setGpToggling(true);
    try {
      await API.put(`/grandparent/child/${gpModal.childId}/link`, { enabled });
      setGpLink(prev => ({ ...prev, enabled }));
    } catch { /* ignore */ }
    finally { setGpToggling(false); }
  };

  const handleRevokeGpLink = async () => {
    if (!gpModal) return;
    const ok = await confirm({
      title: 'Revoke share link?',
      message: 'The current link will stop working immediately. You can generate a new one anytime.',
      confirmLabel: 'Revoke',
      variant: 'danger',
    });
    if (!ok) return;
    setGpRevoking(true);
    try {
      await API.delete(`/grandparent/child/${gpModal.childId}/link`);
      setGpLink(null);
      // Reload a fresh link
      const res = await API.get(`/grandparent/child/${gpModal.childId}/link`);
      setGpLink(res.data.link);
    } catch { /* ignore */ }
    finally { setGpRevoking(false); }
  };

  const toggleInterest = (interest) => {
    setForm(f => ({
      ...f,
      interests: f.interests.includes(interest)
        ? f.interests.filter(i => i !== interest)
        : [...f.interests, interest]
    }));
  };

  const setDayTheme = (day, value) => {
    setForm(f => ({ ...f, weeklySchedule: { ...f.weeklySchedule, [day]: value } }));
  };

  const todayDay = DAYS[new Date().getDay() === 0 ? 6 : new Date().getDay() - 1];
  const todayTheme = (child) => child.weeklySchedule?.[todayDay] || child.weeklyTheme || null;

  const scheduledDaysCount = (child) =>
    DAYS.filter(d => child.weeklySchedule?.[d]).length;

  return (
    <DashboardLayout>

      {/* Page Header */}
      <div className="rounded-4 p-4 mb-4 d-flex align-items-center justify-content-between flex-wrap gap-3"
        style={{background:'linear-gradient(135deg,#f0e8ff 0%,#e8d5ff 100%)', border:'1px solid #d4b8ff'}}>
        <div>
          <h4 className="fw-bold mb-1" style={{color:'var(--text-main)', fontFamily:"'Fredoka One',cursive"}}>
            👶 Child Profiles
          </h4>
          <p className="mb-0 small" style={{color:'#6f42c1'}}>
            Each profile stores your child's age, interests, and story preferences — so every bedtime story is perfectly tailored.
          </p>
        </div>
        {canManageChildren ? (
          <button className="btn btn-dream rounded-pill px-4 fw-semibold" onClick={openAdd}>
            <i className="bi bi-plus-circle me-2"></i>Add Child
          </button>
        ) : (
          /* Say why the controls are absent, rather than leaving a co-parent
             wondering whether the page failed to load. */
          <span className="small text-muted d-flex align-items-center gap-2">
            <i className="bi bi-info-circle"></i>
            Only the primary parent can add or edit profiles
          </span>
        )}
      </div>

      {loading ? (
        <div className="text-center py-5">
          <div className="spinner-border" style={{color:'#6f42c1', width:'3rem', height:'3rem'}}></div>
          <p className="text-muted mt-3">Loading profiles…</p>
        </div>
      ) : children.length === 0 ? (
        <div className="text-center py-5">
          <div style={{fontSize:'80px', marginBottom:'16px'}}>👶</div>
          <h4 className="fw-bold mb-2" style={{color:'var(--text-main)'}}>No child profiles yet</h4>
          <p className="text-muted mb-2" style={{maxWidth:'420px', margin:'0 auto 24px'}}>
            Create a profile for each child so Dreamy Bedtime knows their age, interests, and favourite story style.
          </p>
          <div className="d-flex justify-content-center gap-4 mb-5 flex-wrap">
            {[['🎯','Personalised stories'],['📅','Weekly theme schedules'],['📚','Separate story libraries']].map(([icon, label]) => (
              <div key={label} className="text-center">
                <div className="fs-2 mb-1">{icon}</div>
                <div className="small fw-semibold" style={{color:'#6f42c1'}}>{label}</div>
              </div>
            ))}
          </div>
          <button className="btn btn-dream rounded-pill px-5 fw-bold" onClick={openAdd}>
            <i className="bi bi-plus-circle me-2"></i>Create First Profile
          </button>
        </div>
      ) : (
        <>
          <div className="row g-4 mb-4">
            {children.map(child => {
              const scheduled = scheduledDaysCount(child);
              const tonight = todayTheme(child);
              return (
                <div className="col-md-6 col-lg-4" key={child._id}>
                  <div className="card border-0 shadow-sm h-100 card-hover"
                    style={{borderRadius:'20px', overflow:'hidden'}}>
                    {/* Colour band */}
                    <div style={{height:'6px', background:'linear-gradient(90deg,#6f42c1,#9b6fe8)'}}></div>
                    <div className="card-body p-4">

                      {/* Avatar + name */}
                      <div className="d-flex align-items-center gap-3 mb-3">
                        <div className="d-flex align-items-center justify-content-center rounded-circle shadow-sm"
                          style={{width:'60px', height:'60px', background:'linear-gradient(135deg,#f0e8ff,#e8d5ff)', fontSize:'28px', flexShrink:0}}>
                          {child.avatar}
                        </div>
                        <div>
                          <h5 className="fw-bold mb-0" style={{color:'var(--text-main)'}}>{child.name}</h5>
                          <span className="text-muted small">
                            Age {child.age}
                            {child.gender !== 'neutral' && ` · ${child.gender.charAt(0).toUpperCase()+child.gender.slice(1)}`}
                          </span>
                        </div>
                        <div className="ms-auto d-flex gap-1">
                          {canManageChildren && (
                          <button className="btn btn-sm btn-outline-secondary rounded-circle" style={{width:'32px',height:'32px',padding:0}} onClick={() => openEdit(child)} title="Edit">
                            <i className="bi bi-pencil" style={{fontSize:'12px'}}></i>
                          </button>
                          )}
                          {canManageChildren && (
                          <button className="btn btn-sm rounded-circle" style={{width:'32px',height:'32px',padding:0, background:'#f0e8ff', border:'1px solid #d4b8ff', color:'#6f42c1'}} onClick={() => openGpModal(child)} title="Share with Grandparents">
                            <i className="bi bi-share-fill" style={{fontSize:'12px'}}></i>
                          </button>
                          )}
                          {canManageChildren && (
                          <button className="btn btn-sm btn-outline-danger rounded-circle" style={{width:'32px',height:'32px',padding:0}} onClick={() => handleDelete(child._id)} title="Delete">
                            <i className="bi bi-trash" style={{fontSize:'12px'}}></i>
                          </button>
                          )}
                        </div>
                      </div>

                      {/* Interests */}
                      {child.interests?.length > 0 && (
                        <div className="mb-3 d-flex flex-wrap gap-1">
                          {child.interests.slice(0,6).map(i => (
                            <span key={i} className="badge rounded-pill" style={{background:'var(--purple-section)', color:'#c9a8ff', fontSize:'11px', fontWeight:500}}>{i}</span>
                          ))}
                          {child.interests.length > 6 && (
                            <span className="badge rounded-pill" style={{background:'var(--purple-section)', color:'#c9a8ff', fontSize:'11px'}}>+{child.interests.length-6}</span>
                          )}
                        </div>
                      )}

                      {/* Learning goals badge */}
                      {child.learningGoals?.length > 0 && (
                        <div className="mb-2">
                          <span className="badge rounded-pill d-inline-flex align-items-center gap-1"
                            style={{background:'var(--blue-section)', color:'var(--text-blue)', border:'1px solid #bae6fd', fontSize:'11px', fontWeight:600}}>
                            <i className="bi bi-mortarboard-fill"></i>
                            {child.learningGoals.length} learning goal{child.learningGoals.length !== 1 ? 's' : ''}
                          </span>
                        </div>
                      )}

                      {/* Control badges */}
                      {(child.strictMode || child.requireApproval || child.photoPublicId) && (
                        <div className="mb-3 d-flex flex-wrap gap-1">
                          {child.photoPublicId && (
                            <span className="badge rounded-pill d-inline-flex align-items-center gap-1"
                              style={{background:'#f0fdf4', color:'#059669', border:'1px solid #6ee7b7', fontSize:'11px', fontWeight:600}}>
                              <i className="bi bi-person-bounding-box"></i>
                              {child.usePhotoInIllustrations ? 'Photo Active' : 'Photo Off'}
                            </span>
                          )}
                          {child.strictMode && (
                            <span className="badge rounded-pill d-inline-flex align-items-center gap-1"
                              style={{background:'#fff7ed', color:'#c2410c', border:'1px solid #fed7aa', fontSize:'11px', fontWeight:600}}>
                              <i className="bi bi-shield-check-fill"></i> Strict Mode
                            </span>
                          )}
                          {child.requireApproval && (
                            <span className="badge rounded-pill d-inline-flex align-items-center gap-1"
                              style={{background:'var(--blue-section)', color:'var(--text-blue)', border:'1px solid #bfdbfe', fontSize:'11px', fontWeight:600}}>
                              <i className="bi bi-eye-fill"></i> Approval Queue
                            </span>
                          )}
                        </div>
                      )}

                      {/* Tonight's theme */}
                      {tonight && (
                        <div className="mb-3 p-2 rounded-3 d-flex align-items-center gap-2"
                          style={{background:'#fffbeb', border:'1px solid #fde68a'}}>
                          <i className="bi bi-moon-stars-fill" style={{color:'#d97706'}}></i>
                          <span className="small fw-semibold" style={{color:'#92400e'}}>Tonight: {tonight}</span>
                        </div>
                      )}

                      {/* Stats footer */}
                      <div className="border-top pt-3 mt-auto">
                        <div className="d-flex align-items-center justify-content-between mb-2">
                          <div className="d-flex gap-3 small text-muted">
                            <span><i className="bi bi-book me-1"></i>{child.storiesGenerated || 0} stories</span>
                            {scheduled > 0 && (
                              <span><i className="bi bi-calendar-check me-1"></i>{scheduled} days</span>
                            )}
                          </div>
                          <Link to="/dashboard/generate" className="btn btn-sm btn-dream rounded-pill px-3" style={{fontSize:'12px'}}>
                            <i className="bi bi-magic me-1"></i>Generate
                          </Link>
                        </div>
                        <div className="d-flex gap-2">
                          <Link to={`/dashboard/children/${child._id}/report`}
                            className="btn btn-sm btn-outline-secondary rounded-pill flex-grow-1"
                            style={{fontSize:'12px'}}>
                            <i className="bi bi-bar-chart-fill me-1" style={{color:'#6f42c1'}}></i>
                            Reading Report
                          </Link>
                          <Link to={`/dashboard/children/${child._id}/wrapped`}
                            className="btn btn-sm rounded-pill flex-grow-1"
                            style={{fontSize:'12px', background:'linear-gradient(135deg,#2d1b69,#6f42c1)', color:'white'}}>
                            <i className="bi bi-stars me-1"></i>Wrapped
                          </Link>
                        </div>
                      </div>
                    </div>
                  </div>
                </div>
              );
            })}

            {/* Add another card */}
            <div className="col-md-6 col-lg-4">
              <div className="card border-0 h-100 d-flex align-items-center justify-content-center"
                style={{borderRadius:'20px', border:'2px dashed #c9a8ff', cursor:'pointer', minHeight:'180px'}}
                onClick={openAdd}>
                <div className="text-center p-4">
                  <div className="rounded-circle d-flex align-items-center justify-content-center mx-auto mb-2"
                    style={{width:'50px', height:'50px', background:'var(--purple-section)'}}>
                    <i className="bi bi-plus-lg" style={{color:'#6f42c1', fontSize:'20px'}}></i>
                  </div>
                  <div className="fw-semibold small" style={{color:'#6f42c1'}}>Add another child</div>
                </div>
              </div>
            </div>
          </div>

          {/* Weekly scheduler info */}
          {!hasPaidPlan && (
            <div className="rounded-4 p-4 d-flex align-items-start gap-3"
              style={{background:'var(--header-bg)', border:'1px dashed #c9a8ff'}}>
              <div className="rounded-3 d-flex align-items-center justify-content-center flex-shrink-0"
                style={{width:'48px', height:'48px', background:'var(--purple-section)'}}>
                <i className="bi bi-calendar-week" style={{color:'#6f42c1', fontSize:'22px'}}></i>
              </div>
              <div>
                <div className="fw-bold mb-1" style={{color:'var(--text-main)'}}>Unlock Weekly Theme Schedules</div>
                <p className="text-muted small mb-2">
                  Set Mon = Courage, Tue = Sharing, Wed = Friendship — themes auto-apply each night so you never have to choose. Available on Basic and Family plans.
                </p>
                <Link to="/pricing" className="btn btn-sm btn-dream rounded-pill px-3">Upgrade to Basic</Link>
              </div>
            </div>
          )}
        </>
      )}

      {/* Grandparent Share Modal */}
      {gpModal && (
        <div className="modal show d-block" style={{background:'rgba(0,0,0,0.55)', zIndex:1060}}>
          <div className="modal-dialog modal-dialog-centered" style={{maxWidth:480}}>
            <div className="modal-content border-0" style={{borderRadius:24}}>

              {/* Header */}
              <div className="modal-header border-0 px-4 pt-4 pb-2">
                <div>
                  <h5 className="modal-title fw-bold mb-1" style={{color:'var(--text-main)', fontFamily:"'Fredoka One',cursive", fontSize:22}}>
                    {gpModal.childAvatar} Share {gpModal.childName}'s Library
                  </h5>
                  <p className="text-muted small mb-0">
                    Anyone with this link can read {gpModal.childName}'s stories — no account needed.
                  </p>
                </div>
                <button className="btn-close ms-3" onClick={closeGpModal}></button>
              </div>

              <div className="modal-body px-4 pb-4">
                {/* How it works */}
                <div className="rounded-3 p-3 mb-4" style={{background:'var(--purple-section)', border:'1px solid var(--border-purple)'}}>
                  <div className="fw-semibold small mb-2" style={{color:'var(--text-main)'}}>How it works</div>
                  <div className="d-flex flex-column gap-1">
                    {[
                      ['📤','Share the link with grandparents, family, or friends'],
                      ['📖','They can read all of ' + gpModal.childName + "'s stories, no sign-up needed"],
                      ['🎁','They can gift a subscription directly from the page'],
                    ].map(([icon, text]) => (
                      <div key={text} className="d-flex align-items-start gap-2 small" style={{color:'var(--text-body)'}}>
                        <span style={{flexShrink:0}}>{icon}</span><span>{text}</span>
                      </div>
                    ))}
                  </div>
                </div>

                {gpLoading ? (
                  <div className="text-center py-4">
                    <span className="spinner-border spinner-border-sm" style={{color:'#6f42c1'}}></span>
                  </div>
                ) : gpLink?.error ? (
                  <div className="alert alert-danger small">{gpLink.error}</div>
                ) : gpLink ? (
                  <>
                    {/* Link + copy */}
                    <div className="mb-3">
                      <label className="form-label fw-semibold small" style={{color:'var(--text-main)'}}>Share Link</label>
                      <div className="input-group">
                        <input
                          type="text"
                          className="form-control form-control-sm"
                          readOnly
                          value={gpLink.url}
                          style={{background:'var(--header-bg)', color:'var(--text-body)', borderRight:'none', fontSize:12}}
                        />
                        <button
                          className="btn btn-sm"
                          style={{background: gpCopied ? '#dcfce7' : '#f0e8ff', color: gpCopied ? '#15803d' : '#6f42c1', border:'1px solid var(--border-purple)', borderLeft:'none', fontWeight:700, fontSize:12, minWidth:70}}
                          onClick={handleCopyGpLink}>
                          {gpCopied ? '✓ Copied!' : 'Copy'}
                        </button>
                      </div>
                    </div>

                    {/* Stats */}
                    {gpLink.viewCount > 0 && (
                      <div className="d-flex gap-3 mb-3">
                        <div className="small" style={{color:'var(--text-muted2)'}}>
                          <i className="bi bi-eye me-1"></i>{gpLink.viewCount} view{gpLink.viewCount !== 1 ? 's' : ''}
                        </div>
                        {gpLink.lastViewedAt && (
                          <div className="small" style={{color:'var(--text-muted2)'}}>
                            <i className="bi bi-clock me-1"></i>Last viewed {new Date(gpLink.lastViewedAt).toLocaleDateString('en-US', {month:'short', day:'numeric'})}
                          </div>
                        )}
                      </div>
                    )}

                    {/* Enable / disable toggle */}
                    <div className="d-flex align-items-center justify-content-between p-3 rounded-3 mb-3" style={{background:'var(--header-bg)'}}>
                      <div>
                        <div className="fw-semibold small" style={{color:'var(--text-main)'}}>Link active</div>
                        <div className="text-muted" style={{fontSize:12}}>
                          {gpLink.enabled ? 'Anyone with the link can view stories' : 'Link is paused — no one can view'}
                        </div>
                      </div>
                      <div className="form-check form-switch mb-0">
                        <input className="form-check-input" type="checkbox" role="switch"
                          style={{cursor: gpToggling ? 'not-allowed' : 'pointer', width:40, height:22}}
                          checked={!!gpLink.enabled}
                          disabled={gpToggling}
                          onChange={e => handleToggleGpLink(e.target.checked)} />
                      </div>
                    </div>

                    {/* Share buttons row */}
                    <div className="d-flex gap-2 flex-wrap mb-3">
                      <a
                        href={`https://wa.me/?text=${encodeURIComponent(`Read ${gpModal.childName}'s personalised bedtime stories 🌙 ${gpLink.url}`)}`}
                        target="_blank" rel="noopener noreferrer"
                        className="btn btn-sm rounded-pill flex-grow-1"
                        style={{background:'#25D366', color:'#fff', fontWeight:700, fontSize:12}}>
                        <i className="bi bi-whatsapp me-1"></i>WhatsApp
                      </a>
                      <a
                        href={`mailto:?subject=${encodeURIComponent(`Read ${gpModal.childName}'s bedtime stories`)}&body=${encodeURIComponent(`Hi! Here are ${gpModal.childName}'s personalised bedtime stories: ${gpLink.url}`)}`}
                        className="btn btn-sm btn-outline-secondary rounded-pill flex-grow-1"
                        style={{fontSize:12}}>
                        <i className="bi bi-envelope me-1"></i>Email
                      </a>
                    </div>

                    {/* Revoke */}
                    <div className="text-center mt-1">
                      <button className="btn btn-link btn-sm text-danger p-0" style={{fontSize:12}}
                        onClick={handleRevokeGpLink} disabled={gpRevoking}>
                        {gpRevoking ? 'Revoking…' : 'Revoke link & generate a new one'}
                      </button>
                    </div>
                  </>
                ) : null}
              </div>
            </div>
          </div>
        </div>
      )}

      {/* Add/Edit Modal */}
      {showForm && (
        <div className="modal show d-block" style={{background:'rgba(0,0,0,0.55)', zIndex:1050}}>
          <div className="modal-dialog modal-lg modal-dialog-scrollable modal-dialog-centered">
            <div className="modal-content border-0" style={{borderRadius:'24px'}}>

              <div className="modal-header border-0 px-4 pt-4 pb-2">
                <div>
                  <h5 className="modal-title fw-bold mb-0" style={{color:'var(--text-main)', fontFamily:"'Fredoka One',cursive", fontSize:'22px'}}>
                    {editing ? '✏️ Edit Profile' : '✨ New Child Profile'}
                  </h5>
                  <p className="text-muted small mb-0 mt-1">
                    {editing ? 'Update your child\'s preferences and schedule.' : 'Tell us about your child so every story feels personal.'}
                  </p>
                </div>
                <button className="btn-close ms-auto" onClick={() => setShowForm(false)}></button>
              </div>

              <div className="modal-body px-4 pb-4">
                {error && <div className="alert alert-danger py-2 small rounded-3">{error}</div>}
                <form onSubmit={handleSave}>

                  {/* Avatar */}
                  <div className="mb-4">
                    <label className="form-label fw-semibold small" style={{color:'var(--text-main)'}}>
                      <i className="bi bi-emoji-smile me-1"></i>Choose an Avatar
                    </label>
                    <div className="d-flex flex-wrap gap-2">
                      {AVATARS.map(a => (
                        <button type="button" key={a} onClick={() => setForm(f => ({...f, avatar:a}))}
                          className="btn btn-sm rounded-circle"
                          style={{width:'46px', height:'46px', fontSize:'22px', padding:0,
                            border: form.avatar === a ? '2px solid #6f42c1' : '2px solid #dee2e6',
                            background: form.avatar === a ? '#f0e8ff' : 'white',
                            boxShadow: form.avatar === a ? '0 0 0 3px rgba(111,66,193,0.2)' : 'none',
                          }}>
                          {a}
                        </button>
                      ))}
                    </div>
                  </div>

                  {/* Name / Age / Gender */}
                  <div className="row g-3 mb-4">
                    <div className="col-md-5">
                      <label className="form-label fw-semibold small" style={{color:'var(--text-main)'}}>Child's Name *</label>
                      <input className="form-control" placeholder="e.g. Emma" value={form.name}
                        onChange={e => setForm(f => ({...f, name:e.target.value}))} required />
                    </div>
                    <div className="col-md-3">
                      <label className="form-label fw-semibold small" style={{color:'var(--text-main)'}}>Age *</label>
                      <input type="number" className="form-control" min="1" max="12" placeholder="5"
                        value={form.age} onChange={e => setForm(f => ({...f, age:e.target.value}))} required />
                    </div>
                    <div className="col-md-4">
                      <label className="form-label fw-semibold small" style={{color:'var(--text-main)'}}>Gender</label>
                      <select className="form-select" value={form.gender} onChange={e => setForm(f => ({...f, gender:e.target.value}))}>
                        <option value="neutral">Neutral</option>
                        <option value="girl">Girl</option>
                        <option value="boy">Boy</option>
                      </select>
                    </div>
                  </div>

                  {/* Interests */}
                  <div className="mb-4">
                    <label className="form-label fw-semibold small" style={{color:'var(--text-main)'}}>
                      <i className="bi bi-stars me-1"></i>Interests
                      <span className="text-muted fw-normal ms-1">(tap to select)</span>
                    </label>
                    <div className="d-flex flex-wrap gap-2">
                      {INTERESTS_OPTIONS.map(i => (
                        <button type="button" key={i} onClick={() => toggleInterest(i)}
                          className={`btn btn-sm rounded-pill ${form.interests.includes(i) ? 'btn-dream' : 'btn-outline-secondary'}`}>
                          {i}
                        </button>
                      ))}
                    </div>
                  </div>

                  {/* Characters + Preferences */}
                  <div className="row g-3 mb-4">
                    <div className="col-md-6">
                      <label className="form-label fw-semibold small" style={{color:'var(--text-main)'}}>
                        Favourite Characters <span className="text-muted fw-normal">(comma separated)</span>
                      </label>
                      <input className="form-control" placeholder="Dragons, Unicorns, Talking Cats"
                        value={form.favoriteCharacters} onChange={e => setForm(f => ({...f, favoriteCharacters:e.target.value}))} />
                    </div>
                    <div className="col-md-3">
                      <label className="form-label fw-semibold small" style={{color:'var(--text-main)'}}>Story Length</label>
                      <select className="form-select" value={form.storyPreferences?.length || 'medium'}
                        onChange={e => setForm(f => ({...f, storyPreferences:{...f.storyPreferences, length:e.target.value}}))}>
                        <option value="short">Short (3–4 min)</option>
                        <option value="medium">Medium (5–7 min)</option>
                        <option value="long">Long (8–10 min)</option>
                      </select>
                    </div>
                    <div className="col-md-3">
                      <label className="form-label fw-semibold small" style={{color:'var(--text-main)'}}>Default Tone</label>
                      <select className="form-select" value={form.storyPreferences?.tone || 'magical'}
                        onChange={e => setForm(f => ({...f, storyPreferences:{...f.storyPreferences, tone:e.target.value}}))}>
                        {TONES.map(t => (
                          <option key={t} value={t}>{TONE_INFO[t]} {t.charAt(0).toUpperCase()+t.slice(1)}</option>
                        ))}
                      </select>
                    </div>
                  </div>

                  {/* Strict Mode / Parental Controls */}
                  <div className="mb-4 p-3 rounded-3" style={{background:'#fff7ed', border:'1px solid #fed7aa'}}>
                    <div className="d-flex align-items-start justify-content-between gap-3">
                      <div>
                        <div className="fw-semibold small mb-1 d-flex align-items-center gap-2" style={{color:'#9a3412'}}>
                          <i className="bi bi-shield-check-fill" style={{color:'#ea580c'}}></i>
                          Strict Mode
                          <span className="badge rounded-pill text-white" style={{background:'#ea580c', fontSize:'10px'}}>Parental Control</span>
                        </div>
                        <p className="text-muted small mb-0" style={{fontSize:'12px'}}>
                          When on: no scary elements, no conflict, no villains — only gentle challenges and guaranteed happy, peaceful endings.
                          {form.storyPreferences?.tone === 'spooky' && form.strictMode && (
                            <span className="ms-1 fw-semibold" style={{color:'#c2410c'}}> (Overrides "Spooky" tone)</span>
                          )}
                        </p>
                      </div>
                      <div className="form-check form-switch mb-0 flex-shrink-0" style={{paddingLeft:'2.5rem'}}>
                        <input className="form-check-input" type="checkbox" role="switch"
                          id="strictModeToggle"
                          checked={!!form.strictMode}
                          onChange={e => setForm(f => ({...f, strictMode: e.target.checked}))}
                          style={{width:'2.5rem', height:'1.3rem', cursor:'pointer',
                            backgroundColor: form.strictMode ? '#ea580c' : undefined,
                            borderColor: form.strictMode ? '#ea580c' : undefined}} />
                      </div>
                    </div>
                  </div>

                  {/* Reading Age Calibration */}
                  <div className="mb-4 p-3 rounded-3" style={{background:'#f0fdf4', border:'1px solid #bbf7d0'}}>
                    <div className="d-flex align-items-center justify-content-between mb-2 flex-wrap gap-2">
                      <div className="fw-semibold small d-flex align-items-center gap-2" style={{color:'#14532d'}}>
                        <i className="bi bi-bar-chart-steps" style={{color:'#16a34a'}}></i>
                        Reading Level
                        {form.readingLevel === null ? (
                          <span className="badge rounded-pill" style={{background:'#dcfce7', color:'#15803d', fontSize:'10px', fontWeight:700}}>
                            Auto · Age {form.age || '?'}
                          </span>
                        ) : (
                          <span className="badge rounded-pill" style={{background:'#16a34a', color:'#fff', fontSize:'10px', fontWeight:700}}>
                            Override · Level {form.readingLevel}
                          </span>
                        )}
                      </div>
                      {form.readingLevel !== null && (
                        <button type="button" className="btn btn-sm rounded-pill px-3"
                          style={{background:'#dcfce7', color:'#15803d', fontSize:'11px', fontWeight:600}}
                          onClick={() => setForm(f => ({...f, readingLevel: null}))}>
                          <i className="bi bi-arrow-counterclockwise me-1"></i>Reset to Auto
                        </button>
                      )}
                    </div>

                    <p className="text-muted small mb-3" style={{fontSize:'12px'}}>
                      Controls vocabulary complexity and sentence length in generated stories.
                      Auto matches the child's age. Override for advanced or early readers.
                    </p>

                    <div className="px-1">
                      <input type="range" className="form-range" min="1" max="12" step="1"
                        value={form.readingLevel ?? (parseInt(form.age) || 5)}
                        onChange={e => setForm(f => ({...f, readingLevel: parseInt(e.target.value)}))}
                        style={{accentColor:'#16a34a'}} />
                      <div className="d-flex justify-content-between" style={{fontSize:'11px', color:'#6b7280', marginTop:'2px'}}>
                        <span>1 · Toddler</span>
                        <span style={{fontWeight: form.readingLevel != null ? 700 : 400, color: form.readingLevel != null ? '#15803d' : '#6b7280'}}>
                          {READING_LEVEL_LABELS[form.readingLevel ?? (parseInt(form.age) || 5)]}
                          {' '}(Level {form.readingLevel ?? (parseInt(form.age) || 5)})
                        </span>
                        <span>12 · Advanced</span>
                      </div>
                    </div>
                  </div>

                  {/* Approval Queue */}
                  <div className="mb-4 p-3 rounded-3" style={{background:'var(--blue-section)', border:'1px solid #bfdbfe'}}>
                    <div className="d-flex align-items-start justify-content-between gap-3">
                      <div>
                        <div className="fw-semibold small mb-1 d-flex align-items-center gap-2" style={{color:'var(--text-blue)'}}>
                          <i className="bi bi-eye-fill" style={{color:'#2563eb'}}></i>
                          Approval Queue
                          <span className="badge rounded-pill text-white" style={{background:'#2563eb', fontSize:'10px'}}>Parental Control</span>
                        </div>
                        <p className="text-muted small mb-0" style={{fontSize:'12px'}}>
                          When on: every generated story goes to your Approval Queue first. You review it before it appears in the library.
                        </p>
                      </div>
                      <div className="form-check form-switch mb-0 flex-shrink-0" style={{paddingLeft:'2.5rem'}}>
                        <input className="form-check-input" type="checkbox" role="switch"
                          id="requireApprovalToggle"
                          checked={!!form.requireApproval}
                          onChange={e => setForm(f => ({...f, requireApproval: e.target.checked}))}
                          style={{width:'2.5rem', height:'1.3rem', cursor:'pointer',
                            backgroundColor: form.requireApproval ? '#2563eb' : undefined,
                            borderColor: form.requireApproval ? '#2563eb' : undefined}} />
                      </div>
                    </div>
                  </div>

                  {/* Learning Goals */}
                  <div className="mb-4 p-3 rounded-3" style={{background:'var(--blue-section)', border:'1px solid #bae6fd'}}>
                    <div className="d-flex align-items-center justify-content-between mb-1 flex-wrap gap-2">
                      <div className="fw-semibold small d-flex align-items-center gap-2" style={{color:'var(--text-blue)'}}>
                        <i className="bi bi-mortarboard-fill" style={{color:'#0284c7'}}></i>
                        Learning Goals
                        <span className="badge rounded-pill" style={{background:'var(--blue-section-2)', color:'var(--text-blue)', fontSize:'10px', fontWeight:600}}>
                          {form.learningGoals?.length || 0}/10
                        </span>
                      </div>
                    </div>
                    <p className="text-muted small mb-3" style={{fontSize:'12px'}}>
                      Pick up to 10 topics you'd like this month's stories to teach. You'll see progress on the Reading Report.
                    </p>

                    {/* Selected goals */}
                    {form.learningGoals?.length > 0 && (
                      <div className="d-flex flex-wrap gap-1 mb-3">
                        {form.learningGoals.map(goal => (
                          <span key={goal} className="badge d-inline-flex align-items-center gap-1 rounded-pill px-2 py-1"
                            style={{background:'#0284c7', color:'#fff', fontSize:'12px', fontWeight:500}}>
                            {goal}
                            <button type="button"
                              onClick={() => setForm(f => ({...f, learningGoals: f.learningGoals.filter(g => g !== goal)}))}
                              style={{background:'none', border:'none', color:'rgba(255,255,255,0.8)', padding:'0 0 0 4px', cursor:'pointer', fontSize:'13px', lineHeight:1}}>
                              ×
                            </button>
                          </span>
                        ))}
                      </div>
                    )}

                    {/* Topic suggestions by category */}
                    <div style={{maxHeight:'200px', overflowY:'auto', paddingRight:'4px'}}>
                      {LEARNING_TOPICS.map(({ category, topics }) => (
                        <div key={category} className="mb-2">
                          <div className="small fw-semibold mb-1" style={{color:'var(--text-blue)', fontSize:'11px'}}>{category}</div>
                          <div className="d-flex flex-wrap gap-1">
                            {topics.map(topic => {
                              const selected = form.learningGoals?.includes(topic);
                              const atMax = !selected && form.learningGoals?.length >= 10;
                              return (
                                <button type="button" key={topic}
                                  disabled={atMax}
                                  onClick={() => {
                                    if (selected) {
                                      setForm(f => ({...f, learningGoals: f.learningGoals.filter(g => g !== topic)}));
                                    } else if (!atMax) {
                                      setForm(f => ({...f, learningGoals: [...(f.learningGoals || []), topic]}));
                                    }
                                  }}
                                  className="btn btn-sm rounded-pill"
                                  style={{
                                    fontSize:'11px', padding:'2px 10px',
                                    background: selected ? '#0284c7' : (dm ? 'rgba(2,132,199,0.15)' : '#e0f2fe'),
                                    color: selected ? '#fff' : (dm ? '#7dd3fc' : '#0369a1'),
                                    border: selected ? '1px solid #0284c7' : '1px solid #bae6fd',
                                    opacity: atMax ? 0.5 : 1,
                                  }}>
                                  {selected && <i className="bi bi-check2 me-1" style={{fontSize:'10px'}}></i>}
                                  {topic}
                                </button>
                              );
                            })}
                          </div>
                        </div>
                      ))}
                    </div>

                    {/* Custom goal input */}
                    <div className="d-flex gap-2 mt-3">
                      <input
                        type="text"
                        className="form-control form-control-sm"
                        placeholder="Add a custom topic…"
                        value={customGoalInput}
                        onChange={e => setCustomGoalInput(e.target.value)}
                        onKeyDown={e => {
                          if (e.key === 'Enter') {
                            e.preventDefault();
                            const val = customGoalInput.trim();
                            if (val && !form.learningGoals?.includes(val) && (form.learningGoals?.length || 0) < 10) {
                              setForm(f => ({...f, learningGoals: [...(f.learningGoals || []), val]}));
                              setCustomGoalInput('');
                            }
                          }
                        }}
                        maxLength={100}
                        disabled={(form.learningGoals?.length || 0) >= 10}
                        style={{fontSize:'12px'}}
                      />
                      <button
                        type="button"
                        className="btn btn-sm rounded-pill px-3"
                        style={{background:'#0284c7', color:'#fff', fontSize:'12px', whiteSpace:'nowrap'}}
                        disabled={(form.learningGoals?.length || 0) >= 10 || !customGoalInput.trim()}
                        onClick={() => {
                          const val = customGoalInput.trim();
                          if (val && !form.learningGoals?.includes(val)) {
                            setForm(f => ({...f, learningGoals: [...(f.learningGoals || []), val]}));
                            setCustomGoalInput('');
                          }
                        }}>
                        Add
                      </button>
                    </div>
                  </div>

                  {/* Weekly Theme Scheduler */}
                  {hasPaidPlan ? (
                    <div className="mb-4 p-3 rounded-3" style={{background:'var(--header-bg)', border:'1px solid #e8d5ff'}}>
                      <label className="form-label fw-semibold small d-flex align-items-center gap-2 mb-1" style={{color:'var(--text-main)'}}>
                        <i className="bi bi-calendar-week" style={{color:'#6f42c1'}}></i>
                        Weekly Theme Scheduler
                        <span className="badge rounded-pill text-white" style={{background:'#6f42c1', fontSize:'10px'}}>Auto-applies each night</span>
                      </label>
                      <p className="text-muted small mb-3">Set a theme for each night — it's automatically picked up when you generate a story that day.</p>
                      <div className="row g-2">
                        {DAYS.map((day, i) => (
                          <div className="col-6 col-md-3" key={day}>
                            <label className="form-label small fw-semibold mb-1" style={{color:'#6f42c1'}}>{DAY_LABELS[i]}</label>
                            <select className="form-select form-select-sm"
                              value={form.weeklySchedule?.[day] || ''}
                              onChange={e => setDayTheme(day, e.target.value)}>
                              {SCHEDULER_THEMES.map(t => (
                                <option key={t} value={t}>{t || '— None —'}</option>
                              ))}
                            </select>
                          </div>
                        ))}
                      </div>
                    </div>
                  ) : (
                    <div className="mb-4 p-3 rounded-3 d-flex align-items-start gap-3"
                      style={{background:'var(--header-bg)', border:'1px dashed #c9a8ff'}}>
                      <i className="bi bi-lock-fill mt-1" style={{color:'#6f42c1', fontSize:'18px'}}></i>
                      <div>
                        <div className="fw-semibold small mb-1" style={{color:'var(--text-main)'}}>Weekly Theme Scheduler</div>
                        <p className="text-muted small mb-2">
                          Set Monday = Courage, Tuesday = Sharing, and so on — themes are auto-applied each night.
                        </p>
                        <Link to="/pricing" className="btn btn-sm btn-dream rounded-pill px-3">Upgrade to Basic</Link>
                      </div>
                    </div>
                  )}

                  {/* ── Reference Photo ─────────────────────────────────── */}
                  {flags.photoIllustrations && editing && (
                    <div className="mb-4 p-3 rounded-3" style={{background:'#f0fdf4', border:'1px solid #6ee7b7'}}>
                      <div className="fw-semibold small mb-1 d-flex align-items-center gap-2" style={{color:'#065f46'}}>
                        <i className="bi bi-person-bounding-box" style={{color:'#059669'}}></i>
                        Reference Photo
                        <span className="badge rounded-pill text-white" style={{background:'#059669', fontSize:'10px'}}>Personalised Illustrations</span>
                      </div>
                      <p className="text-muted small mb-3" style={{fontSize:'12px'}}>
                        Upload a clear face photo of your child. When enabled, every story illustration will feature a character that looks <strong>exactly like them</strong> — same face, hair, and features — generated using smart identity-preserving technology.
                      </p>

                      {photoError && (
                        <div className="alert alert-danger py-2 small rounded-3 mb-3">{photoError}</div>
                      )}

                      {form.hasPhoto ? (
                        <div className="d-flex align-items-start gap-3 flex-wrap">
                          {/* Photo preview — loaded through authenticated backend proxy */}
                          {photoPreviewLoading ? (
                            <div style={{width:'80px', height:'80px', borderRadius:'12px', border:'2px solid #6ee7b7', flexShrink:0, background:'#f0fdf4', display:'flex', alignItems:'center', justifyContent:'center'}}>
                              <span className="spinner-border spinner-border-sm" style={{color:'#059669'}}></span>
                            </div>
                          ) : photoPreviewUrl ? (
                            <img
                              src={photoPreviewUrl}
                              alt="Child reference"
                              style={{width:'80px', height:'80px', borderRadius:'12px', objectFit:'cover', border:'2px solid #6ee7b7', flexShrink:0}}
                            />
                          ) : (
                            <div
                              onClick={() => editing && fetchPhotoPreview(editing)}
                              style={{width:'80px', height:'80px', borderRadius:'12px', border:'2px dashed #6ee7b7', flexShrink:0, background:'#f0fdf4', display:'flex', flexDirection:'column', alignItems:'center', justifyContent:'center', cursor:'pointer', gap:'4px'}}
                              title="Click to reload preview">
                              <i className="bi bi-arrow-clockwise" style={{color:'#059669', fontSize:'18px'}}></i>
                              <span style={{fontSize:'9px', color:'#059669'}}>Reload</span>
                            </div>
                          )}
                          <div style={{flex:1, minWidth:'180px'}}>
                            <div className="mb-2 p-2 rounded-3" style={{background:'#dcfce7', border:'1px solid #a7f3d0'}}>
                              <div className="small fw-semibold mb-1" style={{color:'#065f46', fontSize:'11px'}}>
                                <i className="bi bi-stars me-1"></i>Ready — illustrations will feature your child's exact face
                              </div>
                              {form.photoDescription && (
                                <p className="mb-0 small fst-italic" style={{color:'#047857', fontSize:'11px'}}>"{form.photoDescription}"</p>
                              )}
                            </div>
                            {/* Toggle */}
                            <div className="d-flex align-items-center justify-content-between">
                              <div className="small fw-semibold" style={{color:'#065f46', fontSize:'12px'}}>
                                Use in illustrations
                                <span className="ms-2 badge rounded-pill px-2"
                                  style={{background: photoCreditsLeft > 0 ? '#dcfce7' : '#fee2e2',
                                          color:      photoCreditsLeft > 0 ? '#065f46' : '#991b1b',
                                          fontSize:'10px', fontWeight:600}}>
                                  {photoCreditsLeft} credit{photoCreditsLeft !== 1 ? 's' : ''} left
                                </span>
                                {photoCreditsLeft === 0 && (
                                  <button type="button" className="btn btn-link p-0 ms-1"
                                    style={{fontSize:'11px', color:'#db2777', textDecoration:'underline', verticalAlign:'baseline'}}
                                    onClick={() => setShowPhotoTopUp(true)}>
                                    Top up
                                  </button>
                                )}
                              </div>
                              <div className="form-check form-switch mb-0" style={{paddingLeft:'2.5rem'}}>
                                <input
                                  className="form-check-input"
                                  type="checkbox"
                                  role="switch"
                                  checked={!!form.usePhotoInIllustrations}
                                  onChange={e => setForm(f => ({...f, usePhotoInIllustrations: e.target.checked}))}
                                  style={{width:'2.5rem', height:'1.3rem', cursor:'pointer',
                                    backgroundColor: form.usePhotoInIllustrations ? '#059669' : undefined,
                                    borderColor:     form.usePhotoInIllustrations ? '#059669' : undefined}}
                                />
                              </div>
                            </div>
                            <div className="d-flex gap-2 mt-2">
                              <label className="btn btn-sm btn-outline-success rounded-pill px-3" style={{cursor:'pointer', fontSize:'12px'}}>
                                {photoUploading
                                  ? <><span className="spinner-border spinner-border-sm me-1"></span>Uploading…</>
                                  : <><i className="bi bi-arrow-repeat me-1"></i>Replace</>}
                                <input type="file" accept="image/*,image/heic,image/heif" className="d-none" onChange={handlePhotoUpload} disabled={photoUploading} />
                              </label>
                              <button type="button" className="btn btn-sm btn-outline-danger rounded-pill px-3" style={{fontSize:'12px'}} onClick={handlePhotoDelete}>
                                <i className="bi bi-trash me-1"></i>Remove
                              </button>
                            </div>
                          </div>
                        </div>
                      ) : (
                        <label className="btn btn-sm btn-outline-success rounded-pill px-4 w-100" style={{cursor:'pointer', borderStyle:'dashed'}}>
                          {photoUploading
                            ? <><span className="spinner-border spinner-border-sm me-2"></span>Uploading & analysing photo…</>
                            : <><i className="bi bi-camera me-2"></i>Upload Child's Photo</>}
                          <input type="file" accept="image/*,image/heic,image/heif" className="d-none" onChange={handlePhotoUpload} disabled={photoUploading} />
                        </label>
                      )}
                    </div>
                  )}

                  {!editing && (
                    <div className="mb-4 p-3 rounded-3 d-flex align-items-start gap-2"
                      style={{background:'#f0fdf4', border:'1px solid #6ee7b7'}}>
                      <i className="bi bi-info-circle mt-1" style={{color:'#059669', flexShrink:0}}></i>
                      <p className="text-muted small mb-0" style={{fontSize:'12px'}}>
                        <strong style={{color:'#065f46'}}>Reference Photo:</strong> Save this profile first, then edit it to add a photo for personalised illustrations.
                      </p>
                    </div>
                  )}

                  <div className="d-flex gap-2 justify-content-end pt-2">
                    <button type="button" className="btn btn-outline-secondary rounded-pill px-4" onClick={() => setShowForm(false)}>
                      Cancel
                    </button>
                    <button type="submit" className="btn btn-dream rounded-pill px-5 fw-semibold" disabled={saving}>
                      {saving
                        ? <><span className="spinner-border spinner-border-sm me-2"></span>Saving…</>
                        : editing ? '✓ Save Changes' : '✨ Create Profile'}
                    </button>
                  </div>
                </form>
              </div>
            </div>
          </div>
        </div>
      )}

      <TopUpModal
        show={showPhotoTopUp}
        onClose={() => { setShowPhotoTopUp(false); refreshLimits(); }}
        packType="photoIllustrations"
        planName={entitledPlan(user)}
      />
    </DashboardLayout>
  );
};

export default ChildrenPage;
